/**
 * Dismiss Booking Modal
 * 
 * Allows employees to dismiss scheduled bookings with optional reason
 */

import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DISMISSAL_REASONS } from '@/types/scheduledBooking';
import type { DismissalReason } from '@/types/scheduledBooking';

interface DismissBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: {
    projectName: string;
    scheduledHours: {
      monday: number;
      tuesday: number;
      wednesday: number;
      thursday: number;
      friday: number;
      saturday: number;
      sunday: number;
    };
  };
  onConfirm: (reason: DismissalReason, customReason?: string) => void;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function DismissBookingModal({
  open,
  onOpenChange,
  booking,
  onConfirm,
}: DismissBookingModalProps) {
  const [selectedReason, setSelectedReason] = useState<DismissalReason | ''>('');
  const [customReason, setCustomReason] = useState('');

  const totalScheduledHours = DAYS.reduce(
    (sum, day) => sum + booking.scheduledHours[day],
    0
  );

  const workingDays = DAYS.filter(day => booking.scheduledHours[day] > 0);
  const daysText = workingDays.length > 0
    ? workingDays.map(d => d.substring(0, 3)).join(', ')
    : 'No days';

  const handleConfirm = () => {
    if (!selectedReason) return;
    
    const reason = selectedReason === 'other' && customReason
      ? customReason
      : selectedReason;
    
    onConfirm(selectedReason, selectedReason === 'other' ? customReason : undefined);
    
    // Reset form
    setSelectedReason('');
    setCustomReason('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setCustomReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dismiss Scheduled Booking?</DialogTitle>
          <DialogDescription>
            This will remove the scheduled suggestion but keep the row on your timesheet.
            You can still add hours manually if you worked on this project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Booking Summary */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <div className="font-semibold">{booking.projectName}</div>
                <div className="text-sm text-muted-foreground">
                  Scheduled: {daysText} ({totalScheduledHours}h total)
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* Reason Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Why are you dismissing this? (Optional)
            </Label>
            <RadioGroup
              value={selectedReason}
              onValueChange={(value) => setSelectedReason(value as DismissalReason)}
            >
              {DISMISSAL_REASONS.map((reason) => (
                <div key={reason.value} className="flex items-start space-x-2">
                  <RadioGroupItem
                    value={reason.value}
                    id={reason.value}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={reason.value}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {reason.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Custom Reason Input */}
            {selectedReason === 'other' && (
              <div className="ml-6 mt-2">
                <Input
                  placeholder="Please specify..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  className="text-sm"
                />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            className="w-full sm:w-auto"
          >
            Dismiss Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
